import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GearTable } from "../components/GearTable";
import GearTabs from "../components/gear/GearTabs";
import GearStatsRow from "../components/gear/GearStatsRow";
import { api } from "../lib/api";
import { useGearStore } from "../store/gearStore";

const Gear = ({ setSelectedGear, setCurrentPage, setTopbarLeft }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState("equipment");
  const [stats, setStats] = useState(null);

  const refreshStats = async () => {
    try {
      const res = await api.get("/gear/stats");
      const data =
        res?.data && typeof res.data === "object" ? res.data :
        typeof res === "object" ? res :
        null;
      setStats(data);
    } catch {
      setStats(null);
    }
  };

  const { gear, setGear } = useGearStore();
  const safeGear = Array.isArray(gear) ? gear : [];

  // Load gear once per visit (prevents re-fetch on tab switches / table remounts)
  useEffect(() => {
    if (safeGear.length > 0) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/gear");
        const list = Array.isArray(res) ? res : Array.isArray(res?.gear) ? res.gear : [];
        if (!cancelled) setGear(list);
      } catch {
        if (!cancelled) setGear((prev) => (Array.isArray(prev) ? prev : []));
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loadingStats, setLoadingStats] = useState(true);

  // URL-driven tabs: /gear?tab=archive (refresh + shareable)
  useEffect(() => {
    const q = (searchParams.get("tab") || "").toLowerCase();
    const allowed = ["equipment", "maintenance", "events", "archive"];
    if (allowed.includes(q) && q !== activeTab) {
      setActiveTab(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setTab = (next) => {
    setActiveTab(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", next);
      return p;
    });
  };

  // Desktop: inject Gear tabs into the global top row (mobile unchanged)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth < 768) return; // md breakpoint
    if (typeof setTopbarLeft !== "function") return;

    setTopbarLeft(
      <GearTabs
        activeTab={activeTab}
        onChange={setTab}
        variant="bare"
      />
    );

    return () => setTopbarLeft(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, setTopbarLeft]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/gear/stats");

        const data =
          res?.data && typeof res.data === "object" ? res.data :
          typeof res === "object" ? res :
          null;

        if (!mounted) return;
        setStats(data);
      } catch (e) {
        if (!mounted) return;
        setStats(null);
      } finally {
        if (!mounted) return;
        setLoadingStats(false);
      }
    })();

    return () => { mounted = false; };
  }, []);

  return (
    <div className="w-full px-3 sm:px-4 pt-[44px] md:pt-0 pb-4 sm:pb-6">
      {/* Mobile: tabs closer to the top bar */}
      <div className="md:hidden -mt-6">
        <GearTabs
          activeTab={activeTab}
          onChange={setTab}
        />
      </div>

      {activeTab === "equipment" && (
        <GearTable
          setSelectedGear={setSelectedGear}
          setCurrentPage={setCurrentPage}
          archiveMode={false}
          onActiveCountDelta={(delta) => {
            setStats((prev) => prev ? { ...prev, totalAssets: Math.max(0, (prev.totalAssets || 0) + delta) } : prev);
          }}
          onRefreshStats={refreshStats}
          statsRow={
            <GearStatsRow
              stats={stats}
              loading={loadingStats}
              onSelect={(key) => {
                if (key === "assets") setTab("equipment");
                if (key === "actions") setTab("maintenance");
                if (key === "inspections") {
                  setCurrentPage && setCurrentPage("inspections");
                  navigate("/inspections");
                }
              }}
            />
          }
        />
      )}

      {activeTab === "maintenance" && (() => {
        const pending = safeGear
          .filter((g) => !g.isArchived && g.pendingTransfer)
          .sort((a, b) => new Date(b.pendingTransfer?.date || 0) - new Date(a.pendingTransfer?.date || 0));

        if (pending.length === 0) {
          return (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-lg font-semibold text-gray-900 mb-1">No upcoming maintenance</div>
              <div className="text-sm text-gray-600">
                You’re all caught up. When transfers or actions are created, they’ll show up here.
              </div>
            </div>
          );
        }

        return (
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
            <div className="text-lg font-semibold text-gray-900 mb-3">Upcoming Maintenance</div>

            <div className="space-y-2">
              {pending.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="w-full text-left border border-gray-200 rounded-xl p-4 hover:bg-gray-50"
                  onClick={() => {
                    setSelectedGear(g);
                    setCurrentPage && setCurrentPage("gearDetail");
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-gray-900">{g.name}</div>
                    <div className="text-xs text-gray-500">
                      {g.pendingTransfer?.date ? new Date(g.pendingTransfer.date).toLocaleString() : ""}
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 mt-1">
                    Transfer pending acceptance
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {activeTab === "events" && (() => {
        const feed = [];

        for (const g of safeGear) {
          const activity = Array.isArray(g.activity) ? g.activity : [];
          for (const act of activity) {
            feed.push({
              type: act?.type || "activity",
              date: act?.date || "",
              gear: g,
              act,
            });
          }

          // Also show pending transfer as an event (not stored in activity)
          if (g.pendingTransfer?.date) {
            feed.push({
              type: "transfer-requested",
              date: g.pendingTransfer.date,
              gear: g,
              act: { ...g.pendingTransfer },
            });
          }
        }

        feed.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

        const labelFor = (row) => {
          // Prefer explicit action text if present (matches GearDetail-style activity)
          if (row.act?.action) return row.act.action;

          if (row.type === "created") return "Added";
          if (row.type === "archived") return "Archived";
          if (row.type === "unarchived") return "Restored";

          if (row.type === "transfer-requested") return "Transfer requested";
          if (row.type === "transfer-accepted") return "Transfer accepted";
          if (row.type === "transfer-denied") return "Transfer denied";
          if (row.type === "transfer") return "Transfer";

          return "Update";
        };

        if (feed.length === 0) {
          return (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="text-lg font-semibold text-gray-900 mb-1">No activity yet</div>
              <div className="text-sm text-gray-600">
                Transfers and updates will build an event history over time.
              </div>
            </div>
          );
        }

        return (
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
            <div className="text-lg font-semibold text-gray-900 mb-3">Event history</div>

            <div className="space-y-2">
              {feed.slice(0, 50).map((row, idx) => (
                <button
                  key={`${row.gear.id}_${row.type}_${idx}`}
                  type="button"
                  className="w-full text-left border border-gray-200 rounded-xl p-4 hover:bg-gray-50"
                  onClick={() => {
                    setSelectedGear(row.gear);
                    setCurrentPage && setCurrentPage("gearDetail");
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-gray-900">{labelFor(row)}</div>
                    <div className="text-xs text-gray-500">
                      {row.date ? new Date(row.date).toLocaleString() : ""}
                    </div>
                  </div>

                  <div className="text-sm text-gray-700 mt-1">
                    {row.gear.name}
                    {row.gear.isArchived ? <span className="text-gray-400"> (Archived)</span> : null}
                  </div>

                  {(row.type === "transfer-accepted" || row.type === "transfer-denied") && (
                    <div className="text-xs text-gray-500 mt-1">
                      {row.type === "transfer-accepted" ? "Transfer completed" : "Transfer declined"}
                    </div>
                  )}

                  {row.type === "transfer-requested" && (
                    <div className="text-xs text-gray-500 mt-1">
                      Pending transfer
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {activeTab === "archive" && (
        <GearTable
          setSelectedGear={setSelectedGear}
          setCurrentPage={setCurrentPage}
          archiveMode={true}
          onActiveCountDelta={(delta) => {
            setStats((prev) => prev ? { ...prev, totalAssets: Math.max(0, (prev.totalAssets || 0) + delta) } : prev);
          }}
          onRefreshStats={refreshStats}
          statsRow={
            <GearStatsRow
              stats={stats}
              loading={loadingStats}
              onSelect={(key) => {
                if (key === "assets") setTab("equipment");
                if (key === "actions") setTab("maintenance");
                if (key === "inspections") {
                  setCurrentPage && setCurrentPage("inspections");
                  navigate("/inspections");
                }
              }}
            />
          }
        />
      )}

    </div>
  );
};

export default Gear;
